import { useEffect, type DependencyList } from 'react';
import { useLatest } from './useLatest';

/**
 * Read data for the current inputs. `load` runs after a render in which one of `deps` changed, with a signal
 * that is aborted as soon as the inputs change again or the component unmounts. Pass the signal to the
 * request and set state only while `!signal.aborted`, so a stale reply never lands.
 *
 * For component-local reads (a dialog's organization, an article, a preview). Server data that several
 * views show belongs in one place they all read, not in a copy per component.
 */
export function useLoad(load: (signal: AbortSignal) => void | Promise<unknown>, deps: DependencyList): void {
    const latest = useLatest(load);
    useEffect(() => {
        const controller = new AbortController();
        const unhandled = (error: unknown) => {
            if (!controller.signal.aborted) console.error('[useLoad] a load failed without handling it', error);
        };
        try {
            void Promise.resolve(latest.current(controller.signal)).catch(unhandled);
        } catch (error) {
            unhandled(error);
        }
        return () => controller.abort();
        // The caller's deps decide when to load again; exhaustive-deps checks them at the call site.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
